import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useRouter } from 'next/router'

import { modalTypes } from '~/components/modalLoader'
import { useUserPortfolio } from '~/containers/AppContainer/PortfolioProvider'
import { useAuthV2 } from '~/contexts/AuthContext'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import { SelectedArtworkContext } from '~/contexts/SelectedArtworkContext'
import { TicketTypes } from '~/data/forms/shared/interfaces/Form'
import { isValuationType, ValuationTypes } from '~/data/valuationTypes'
import usePortfolioTicketCreator from '~/hooks/usePortfolioTicketCreator'
import useValuationJourneyAnalytics, {
  ValuationJourneyStepNames,
} from '~/hooks/useValuationJourneyAnalytics'
import { isTier1Artist } from '~/utils/getArtistTierLevel'

const valuationTypeOrder: ValuationTypes[] = [
  ValuationTypes.expert,
  ValuationTypes.replacement,
  ValuationTypes.instant,
]

export enum ValuationJourneySteps {
  artwork = 'artwork',
  type = 'type',
  auth = 'auth',
  details = 'details',
}

export interface UseValuationJourneyArgs {
  /** Pre-selected valuation type from the launching hero/CTA (CMS driven) */
  preselectedType?: ValuationTypes
  /** Artwork already chosen before the drawer opened (e.g. SSO resume) */
  initialArtwork?: any
  /** Step to resume at (e.g. after an SSO login redirect) */
  initialStep?: ValuationJourneySteps
  /**
   * Artist id fallback for SSO resumes, where `SelectedArtworkContext` has
   * been reset by the redirect
   */
  initialArtistId?: string
  /**
   * When true (SSO resume), automatically continues the journey once the
   * user, artwork and type are all present
   */
  autoResume?: boolean
  /**
   * PostHog formLaunchId to resume with (SSO redirect). In-memory analytics
   * state is lost across the redirect, so it travels in the URL
   */
  initialFormLaunchId?: string
  /** Closes the drawer (provided by PageWrapper as `callbackFn`) */
  closeDrawer?: () => void
}

/**
 * Owns all state/logic for the unified valuations journey drawer.
 *
 * Steps: artwork → type → (auth when logged out) → details (expert/replacement)
 * - instant: creates an `instantValuationRequested` ticket then redirects to
 *   `/valuation` exactly as the previous SellArtworkHero flow did
 * - expert/replacement: continues to the existing valuation slides (details)
 *   and ends at `/valuation-confirmation`
 */
export const useValuationJourney = ({
  preselectedType,
  initialArtwork,
  initialStep,
  initialArtistId,
  autoResume,
  initialFormLaunchId,
  closeDrawer,
}: UseValuationJourneyArgs) => {
  const router = useRouter()
  const { user } = useAuthV2()
  const { doCreate } = usePortfolioTicketCreator()
  const { portfolioDetails, refreshPortfolioDetails } = useUserPortfolio()
  const { state, stateDispatch } = useContext(GlobalStoreContext)
  const { currentArtworkState } = useContext(SelectedArtworkContext)
  const isCreatingTicketRef = useRef(false)

  const [currentStep, setCurrentStep] = useState<ValuationJourneySteps>(
    initialStep ?? ValuationJourneySteps.artwork
  )
  const [selectedArtwork, setSelectedArtwork] = useState<any | null>(
    initialArtwork ?? null
  )
  const [selectedType, setSelectedType] = useState<ValuationTypes | null>(
    preselectedType ?? null
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const artistName = currentArtworkState.artist?.name
  const artistId = currentArtworkState.artist?.id ?? initialArtistId

  const isArtistTier1 = useMemo(
    () => isTier1Artist({ id: artistId, name: artistName }),
    [artistId, artistName]
  )

  const availableTypes = useMemo(
    () =>
      valuationTypeOrder.filter(
        type => type !== ValuationTypes.replacement || isArtistTier1
      ),
    [isArtistTier1]
  )

  /**************************************************************
   * PostHog tagging - one formLaunchId for the whole journey
   **************************************************************/
  const {
    startJourneyAnalytics,
    trackJourneyStepLoaded,
    tagValuationType,
    syncAuthAnalytics,
  } = useValuationJourneyAnalytics()
  const formLaunchId: string | undefined = state.formAnalytics?.formLaunchId

  // The hero CTA seeds the id before opening the drawer. Only seed here when
  // resuming from SSO (id carried in the URL) or when nothing seeded it at
  // all - reopening from the insurance modal's Back keeps the existing state
  const hasSeededAnalyticsRef = useRef(false)
  useEffect(() => {
    if (hasSeededAnalyticsRef.current) return
    hasSeededAnalyticsRef.current = true

    if (initialFormLaunchId || !formLaunchId) {
      startJourneyAnalytics({
        formLaunchId: initialFormLaunchId,
        artistName,
        artistId,
        artworkName: initialArtwork?.label,
        artworkId: initialArtwork?.value,
        valuationType: preselectedType,
      })
    }
  }, [
    initialFormLaunchId,
    formLaunchId,
    startJourneyAnalytics,
    artistName,
    artistId,
    initialArtwork?.label,
    initialArtwork?.value,
    preselectedType,
  ])

  /** Sets the type and tags the journey - the formLaunchId never changes */
  const selectType = useCallback(
    (type: ValuationTypes) => {
      setSelectedType(type)
      tagValuationType(type)
    },
    [tagValuationType]
  )

  // Visible steps for the progress bar. Auth is only shown when logged out;
  // details only applies to expert journeys (instant redirects to /valuation,
  // replacement hands over to the insurance valuation modal).
  const visibleSteps = useMemo(() => {
    const steps: ValuationJourneySteps[] = [
      ValuationJourneySteps.artwork,
      ValuationJourneySteps.type,
    ]
    if (!user) steps.push(ValuationJourneySteps.auth)
    if (selectedType === ValuationTypes.expert) {
      steps.push(ValuationJourneySteps.details)
    }
    return steps
  }, [user, selectedType])

  const stepIndex = Math.max(visibleSteps.indexOf(currentStep), 0)

  // Drawer-owned steps report `Form Step Loaded` with an explicit number
  // (artwork = 1, type = 2, auth = 3 when shown); the embedded FormBuilder on
  // the details step reports its own slides and increments from there. Fires
  // once per step entry, waiting for the id when seeded on mount (SSO resume)
  const lastTrackedStepRef = useRef<ValuationJourneySteps | null>(null)
  useEffect(() => {
    if (!formLaunchId) return
    if (currentStep === ValuationJourneySteps.details) return
    if (lastTrackedStepRef.current === currentStep) return

    const stepNumber = visibleSteps.indexOf(currentStep) + 1
    if (stepNumber < 1) return

    lastTrackedStepRef.current = currentStep
    trackJourneyStepLoaded(ValuationJourneyStepNames[currentStep], stepNumber)

    // Login/signup events fired inside the drawer should carry the journey id
    if (currentStep === ValuationJourneySteps.auth) {
      syncAuthAnalytics()
    }
  }, [
    currentStep,
    formLaunchId,
    visibleSteps,
    trackJourneyStepLoaded,
    syncAuthAnalytics,
  ])

  /**
   * Progress reported by the embedded FormBuilder on the details step
   * (slide index + total slides, thank-you excluded). Combined with the
   * journey's own steps below so a single progress bar spans the whole flow.
   */
  const [formProgress, setFormProgress] = useState<{
    step: number
    total: number
  } | null>(null)

  const handleFormStepChange = useCallback(
    (step: number, totalSteps: number) => {
      setFormProgress({ step, total: totalSteps })
    },
    []
  )

  // Combined progress bar values: on the details step the embedded form's
  // slides replace the single `details` segment, offset by the journey steps
  // already completed (artwork → type → [auth]). Until the lazy-loaded form
  // reports in, fall back to the journey-only values.
  const detailsOffset = visibleSteps.indexOf(ValuationJourneySteps.details)
  const combinedFormProgress =
    currentStep === ValuationJourneySteps.details && detailsOffset >= 0
      ? formProgress
      : null
  const progressSteps = combinedFormProgress
    ? detailsOffset + combinedFormProgress.total
    : visibleSteps.length
  const progressCurrentStep = combinedFormProgress
    ? detailsOffset + combinedFormProgress.step
    : stepIndex

  const continueEnabled = useMemo(() => {
    if (currentStep === ValuationJourneySteps.artwork) {
      return Boolean(selectedArtwork)
    }
    if (currentStep === ValuationJourneySteps.type) {
      return Boolean(selectedType && availableTypes.includes(selectedType))
    }
    return false
  }, [currentStep, selectedArtwork, selectedType, availableTypes])

  /**
   * Instant flow — identical ticket + redirect behaviour to the previous
   * inline SellArtworkHero flow.
   */
  const createInstantTicketAndRedirect = useCallback(async () => {
    if (!selectedArtwork?.value || isCreatingTicketRef.current) return

    isCreatingTicketRef.current = true
    setIsSubmitting(true)

    try {
      const ticketType = TicketTypes.instantValuationRequested
      const currentPage = `${location.protocol}//${location.host}${location.pathname}`
      const properties = {
        artwork: parseInt(selectedArtwork.value.split('-')[1]),
        ticket_type: ticketType,
        call_to_action_url: currentPage,
        artist: artistId,
        sale_urgency: 'just looking for a valuation',
        insurance_requested: false,
      }

      const updateRomeo = await doCreate(properties, ticketType)

      if (updateRomeo?.ticketId || updateRomeo?.status === 'enquiry active') {
        void refreshPortfolioDetails()
        closeDrawer?.()

        const params = new URLSearchParams({ artwork: selectedArtwork.value })
        if (updateRomeo.ticketId) params.set('ti', String(updateRomeo.ticketId))
        // Carry the journey's PostHog id + type so the /valuation page keeps
        // tagging with the same formLaunchId (survives a reload too)
        if (formLaunchId) params.set('formLaunchId', formLaunchId)
        params.set('valuationType', ValuationTypes.instant)

        router.push(`/valuation?${params.toString()}`)
      }
    } finally {
      isCreatingTicketRef.current = false
      setIsSubmitting(false)
    }
  }, [
    selectedArtwork?.value,
    artistId,
    formLaunchId,
    doCreate,
    refreshPortfolioDetails,
    closeDrawer,
    router,
  ])

  /**
   * Replacement flow — hands over to the insurance valuation modal (the
   * same journey `InsuranceValuationCta` launches from the portfolio),
   * landing straight on the artwork-list slide with the chosen artwork
   * pre-checked. If the artwork isn't in the user's portfolio yet, a
   * portfolio (`owned`) ticket is created silently first.
   */
  const launchReplacementFlow = useCallback(async () => {
    if (!selectedArtwork?.value || isCreatingTicketRef.current) return

    isCreatingTicketRef.current = true
    setIsSubmitting(true)

    try {
      // ArtworkSearchSelect values are `artwork-{n}`; portfolio artworks are
      // `aw-{n}` — match on the shared numeric id
      const artworkNumericId = parseInt(selectedArtwork.value.split('-')[1])

      const existingItem = portfolioDetails.processedData?.portfolio?.find(
        (item: any) =>
          parseInt(String(item.artwork?._id ?? '').split('-')[1]) ===
          artworkNumericId
      )

      let ticketId: string | undefined = existingItem?.ticketId
        ? String(existingItem.ticketId)
        : undefined

      if (!ticketId) {
        // Not in the portfolio yet — silently add it (same ticket shape as
        // the add-to-portfolio action) so it appears in the artwork list
        const currentPage = `${location.protocol}//${location.host}${location.pathname}`
        const properties = {
          artwork: artworkNumericId,
          ticket_type: TicketTypes.owned,
          call_to_action_url: currentPage,
          artist: artistId,
        }
        const skipZepicOwnedEvent = true
        const updateRomeo = await doCreate(
          properties,
          TicketTypes.owned,
          skipZepicOwnedEvent
        )
        if (updateRomeo?.ticketId) {
          ticketId = String(updateRomeo.ticketId)
        }
        // The insurance modal reads the portfolio from the provider, so the
        // new ticket must be present before it opens
        await refreshPortfolioDetails()
      }

      // Swap the drawer over to the insurance valuation modal (same props as
      // InsuranceValuationCta), skipping the intro straight to the list
      stateDispatch({
        type: GlobalStoreProps.UPDATE_PREVENT_PAGE_SCROLL,
        payload: true,
      })
      stateDispatch({
        type: GlobalStoreProps.UPDATE_MODAL_STATE,
        payload: {
          isOpen: true,
          component: modalTypes.purchaseInsuranceModal,
          isDrawer: true,
          content: null,
          props: {
            returnToCurrent: true,
            isReducedWidth: true,
            title: '',
            hideModalTitle: true,
            showMpModalHeader: true,
            drawerTheme: undefined,
            insuranceValuationArtworkId: `aw-${artworkNumericId}`,
            insuranceValuationTicketId: ticketId,
            hideCloseButton: true,
            initialSlide: 1,
            // Keep the drawer the same width as the valuation journey
            panelMaxWidth: 720,
            // Lets Back on the artwork-list slide reopen this journey at the
            // type step with the artwork + type preserved
            journeyResume: {
              initialArtwork: selectedArtwork,
              initialArtistId: artistId,
              preselectedType: selectedType ?? undefined,
              initialStep: ValuationJourneySteps.type,
              panelMaxWidth: 720,
            },
          },
        },
      })
    } finally {
      isCreatingTicketRef.current = false
      setIsSubmitting(false)
    }
  }, [
    selectedArtwork,
    artistId,
    selectedType,
    portfolioDetails.processedData?.portfolio,
    doCreate,
    refreshPortfolioDetails,
    stateDispatch,
  ])

  /**
   * Initial values fed into the embedded valuation FormBuilder on the
   * details step. Mirrors the `?form=valuation` initial values shape.
   */
  const detailsInitialValues = useMemo(
    () => ({
      artist: artistId,
      artwork: selectedArtwork?.value,
      artworkName: selectedArtwork?.label,
    }),
    [artistId, selectedArtwork?.value, selectedArtwork?.label]
  )

  /**
   * Expert/replacement journeys end here — close the drawer and land on the
   * confirmation page with enough context to resolve the artwork + T1 hero.
   */
  const handleDetailsComplete = useCallback(() => {
    void refreshPortfolioDetails()
    closeDrawer?.()

    const params = new URLSearchParams()
    if (selectedArtwork?.value) params.set('artwork', selectedArtwork.value)
    if (artistId) params.set('artist', artistId)
    if (selectedType) params.set('type', selectedType)
    void router.push(`/valuation-confirmation?${params.toString()}`)
  }, [
    refreshPortfolioDetails,
    closeDrawer,
    selectedArtwork?.value,
    artistId,
    selectedType,
    router,
  ])

  /**
   * Called once the valuation type is confirmed and the user is
   * authenticated — routes the journey by type.
   */
  const continueAfterAuth = useCallback(async () => {
    if (selectedType === ValuationTypes.instant) {
      await createInstantTicketAndRedirect()
      return
    }
    if (selectedType === ValuationTypes.replacement) {
      // Replacement journeys hand over to the insurance valuation modal
      await launchReplacementFlow()
      return
    }
    // Expert journeys continue to the existing valuation slides
    setCurrentStep(ValuationJourneySteps.details)
  }, [selectedType, createInstantTicketAndRedirect, launchReplacementFlow])

  const goNext = useCallback(async () => {
    if (currentStep === ValuationJourneySteps.artwork) {
      setCurrentStep(ValuationJourneySteps.type)
      return
    }

    if (currentStep === ValuationJourneySteps.type) {
      if (user) {
        await continueAfterAuth()
      } else {
        setCurrentStep(ValuationJourneySteps.auth)
      }
    }
  }, [currentStep, user, continueAfterAuth])

  const goBack = useCallback(() => {
    if (currentStep === ValuationJourneySteps.type) {
      setCurrentStep(ValuationJourneySteps.artwork)
    } else if (currentStep === ValuationJourneySteps.auth) {
      setCurrentStep(ValuationJourneySteps.type)
    } else if (currentStep === ValuationJourneySteps.details) {
      setCurrentStep(ValuationJourneySteps.type)
    }
  }, [currentStep])

  const backEnabled =
    currentStep !== ValuationJourneySteps.artwork && !isSubmitting

  /**
   * Where SSO logins land after the redirect — carries everything the resume
   * hook needs to reopen the drawer at the right step.
   */
  const authOnCompleteUrl = useMemo(() => {
    const basePath = router.asPath.split('?')[0]
    const params = new URLSearchParams()
    if (selectedArtwork?.value) params.set('vjArtwork', selectedArtwork.value)
    // Carry the display label too, so the resumed drawer can show the
    // artwork name (feeds `detailsInitialValues.artworkName`)
    if (selectedArtwork?.label) {
      params.set('vjArtworkLabel', selectedArtwork.label)
    }
    if (artistId) params.set('vjArtist', artistId)
    if (selectedType) params.set('vjType', selectedType)
    // Analytics state is in memory only - relay the id across the redirect
    if (formLaunchId) params.set('vjFormLaunchId', formLaunchId)
    const queryString = params.toString()
    return queryString ? `${basePath}?${queryString}` : basePath
  }, [
    router.asPath,
    selectedArtwork?.value,
    selectedArtwork?.label,
    artistId,
    selectedType,
    formLaunchId,
  ])

  // SSO resume: once the drawer reopens with everything pre-filled and the
  // user authenticated, continue the journey automatically (once)
  const hasAutoResumedRef = useRef(false)
  useEffect(() => {
    if (!autoResume || hasAutoResumedRef.current) return
    if (!user || !selectedArtwork || !selectedType) return

    hasAutoResumedRef.current = true
    void continueAfterAuth()
  }, [autoResume, user, selectedArtwork, selectedType, continueAfterAuth])

  return {
    // state
    currentStep,
    stepIndex,
    visibleSteps,
    progressSteps,
    progressCurrentStep,
    selectedArtwork,
    selectedType,
    availableTypes,
    isSubmitting,
    continueEnabled,
    backEnabled,
    artistName,
    artistId,
    authOnCompleteUrl,
    detailsInitialValues,
    // actions
    setSelectedArtwork,
    selectType,
    goNext,
    goBack,
    continueAfterAuth,
    handleDetailsComplete,
    handleFormStepChange,
  }
}

/**
 * SSO-return resume hook (modelled on `useFormLaunchFromQueryParam`).
 *
 * Mounted by `SellArtworkHero`. When the page loads with `vjArtwork` in the
 * query string and an authenticated user (i.e. we've just landed back from an
 * SSO redirect started inside the drawer), it reopens the
 * `ValuationJourneyDrawer` pre-filled so the journey continues automatically,
 * then strips the `vj*` params from the URL.
 */
export const useValuationJourneyResume = () => {
  const router = useRouter()
  const { user } = useAuthV2()
  const { stateDispatch } = useContext(GlobalStoreContext)
  const hasResumedRef = useRef(false)

  useEffect(() => {
    if (hasResumedRef.current || !router.isReady) return

    const { vjArtwork, vjArtworkLabel, vjArtist, vjType, vjFormLaunchId } =
      router.query
    if (typeof vjArtwork !== 'string' || !vjArtwork || !user) return

    hasResumedRef.current = true

    stateDispatch({
      type: GlobalStoreProps.UPDATE_MODAL_STATE,
      payload: {
        isOpen: true,
        isDrawer: true,
        component: modalTypes.valuationJourney,
        content: null,
        props: {
          initialArtwork: {
            value: vjArtwork,
            label:
              typeof vjArtworkLabel === 'string' && vjArtworkLabel
                ? vjArtworkLabel
                : vjArtwork,
          },
          initialArtistId: typeof vjArtist === 'string' ? vjArtist : undefined,
          preselectedType: isValuationType(vjType) ? vjType : undefined,
          initialFormLaunchId:
            typeof vjFormLaunchId === 'string' && vjFormLaunchId
              ? vjFormLaunchId
              : undefined,
          initialStep: ValuationJourneySteps.type,
          autoResume: true,
          // Wider drawer so the type-selector cards fit in a 3-column grid
          panelMaxWidth: 720,
        },
      },
    })

    // Strip the vj* params so refreshes don't reopen the drawer
    const params = new URLSearchParams(window.location.search)
    params.delete('vjArtwork')
    params.delete('vjArtworkLabel')
    params.delete('vjArtist')
    params.delete('vjType')
    params.delete('vjFormLaunchId')
    const queryString = params.toString()
    const basePath = router.asPath.split('?')[0]
    void router.replace(
      queryString ? `${basePath}?${queryString}` : basePath,
      undefined,
      { shallow: true }
    )
  }, [router, user, stateDispatch])
}
